import { FormProvider } from 'react-hook-form'
import { useNavigate } from 'react-router-dom'

import { useRef } from 'react'

import { useProductForm } from '@/hooks/features/products/useProductForm'
import { AssistantReviewContext, useProductProposal } from '@/components/assistant/useProductProposal'
import { ReviewBanner } from '@/components/assistant/ReviewBanner'

import { Button } from '@/components/ui/button'
import { Tabs, TabsContent } from '@/components/ui/tabs'

import { BackButton, PageHeader, UnderlinedTabsList } from '@/components/atoms'
import { GeneralSection } from '@/components/features/products/form/GeneralSection'
import { SeoSection } from '@/components/features/products/form/SeoSection'
import { VariantsSection } from '@/components/features/products/form/VariantsSection'

export const AddProduct = () => {
  const navigate = useNavigate()
  // The assistant's proposal (if any) is marked saved when the normal save succeeds.
  const savedRef = useRef<() => void>()
  const {
    form,
    onSubmit,
    isSubmitting,
    attributes,
    attributesLoading,
    categories,
    categoriesLoading,
    brands,
    brandsLoading,
  } = useProductForm(undefined, { onSaved: () => savedRef.current?.() })
  const review = useProductProposal(form, { ready: true })
  savedRef.current = review.onSaved

  return (
    <FormProvider {...form}>
      <AssistantReviewContext.Provider value={review.changed}>
      <div className="mx-auto w-full max-w-[1088px] pb-20">
        {review.proposal && <ReviewBanner mode="save" onDiscard={review.discard} className="mb-3" />}
        <PageHeader
          size="large"
          title="Add product"
          back={<BackButton onClick={() => navigate('/products')} label="Back to products" />}
          actions={
            <>
              <Button variant="outline" type="button" onClick={() => navigate('/products')}>
                Cancel
              </Button>
              <Button type="button" disabled={isSubmitting} onClick={form.handleSubmit(onSubmit)}>
                {isSubmitting ? 'Saving…' : 'Save'}
              </Button>
            </>
          }
        />

        <Tabs defaultValue="general" className="w-full">
          <UnderlinedTabsList
            tabs={[
              { label: 'General', value: 'general' },
              { label: 'Variants', value: 'variants' },
              { label: 'SEO', value: 'seo' },
            ]}
          />

          <TabsContent value="general" className="mt-4">
            <GeneralSection
              form={form}
              attributes={attributes}
              attributesLoading={attributesLoading}
              categories={categories}
              categoriesLoading={categoriesLoading}
              brands={brands}
              brandsLoading={brandsLoading}
            />
          </TabsContent>

          <TabsContent value="variants" className="mt-4">
            <VariantsSection
              attributes={attributes}
              attributesLoading={attributesLoading}
              categories={categories}
              categoriesLoading={categoriesLoading}
              brands={brands}
              brandsLoading={brandsLoading}
            />
          </TabsContent>

          <TabsContent value="seo" className="mt-4">
            <SeoSection form={form} />
          </TabsContent>
        </Tabs>
      </div>
      </AssistantReviewContext.Provider>
    </FormProvider>
  )
}
